import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, ChevronDown } from "lucide-react";
import { SERVICES, waLink } from "../data/content";
import { FadeUp, MaskedLine } from "../components/Reveal";
import { CTASection } from "../components/PageHero";
import { SERVICE_ICONS } from "../components/serviceIcons";

const FAQ = ({ items, color }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3">
      {items.map((f, i) => (
        <div key={i} className="hospitality-card bg-white rounded-2xl border border-line overflow-hidden">
          <button
            data-testid={`faq-trigger-${i}`}
            onClick={() => setOpen(open === i ? -1 : i)}
            className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
          >
            <span className="font-bold text-aubergine text-sm sm:text-base">{f.q}</span>
            <ChevronDown className={`w-5 h-5 shrink-0 transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} style={{ color }} />
          </button>
          <AnimatePresence initial={false}>
            {open === i && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35 }}
              >
                <p className="px-6 pb-6 text-sm text-ink-muted leading-relaxed" data-testid={`faq-answer-${i}`}>{f.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.id === slug);

  if (!service) {
    return (
      <main className="pt-28 sm:pt-32 lg:pt-40 pb-32 text-center" data-testid="service-not-found">
        <h1 className="font-display font-extrabold text-4xl text-aubergine">Service not found</h1>
        <Link to="/services" className="mt-6 inline-flex items-center gap-2 font-bold text-terracotta">
          <ArrowLeft className="w-4 h-4" /> Back to services
        </Link>
      </main>
    );
  }

  const Icon = SERVICE_ICONS[service.icon];
  const others = SERVICES.filter((s) => s.id !== service.id);

  return (
    <main data-testid={`service-page-${service.id}`}>
      <section className="hospitality-background relative pt-28 sm:pt-32 lg:pt-40 pb-24 overflow-hidden grain-overlay">
        <div className="absolute top-24 -left-32 w-96 h-96 rounded-full blur-3xl opacity-80 drift-c" style={{ background: service.soft }} />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full bg-saffron-soft blur-3xl opacity-60 drift-b" />
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 relative">
          <FadeUp y={12}>
            <Link to="/services" data-testid="service-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-ink-muted hover:text-terracotta transition-colors">
              <ArrowLeft className="w-4 h-4" /> All services
            </Link>
          </FadeUp>
          <div className="mt-8 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <FadeUp y={14}>
                <span className="hospitality-card inline-flex items-center gap-2.5 bg-white border border-line rounded-full pl-2 pr-4 py-2 shadow-sm">
                  <span className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: service.color }}>
                    <Icon className="w-4 h-4 text-cream" />
                  </span>
                  <span className="text-xs font-mono uppercase tracking-[0.2em] font-semibold" style={{ color: service.color }}>{service.badge}</span>
                </span>
              </FadeUp>
              <h1 className="mt-6 font-display font-extrabold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-aubergine break-words">
                <MaskedLine delay={0.1}>{service.title}</MaskedLine>
              </h1>
              <FadeUp delay={0.3}>
                <p className="mt-3 font-display font-bold text-xl sm:text-2xl" style={{ color: service.color }}>{service.tagline}</p>
              </FadeUp>
              <FadeUp delay={0.4}>
                <p className="mt-6 text-base sm:text-lg text-ink-muted leading-relaxed">{service.short}</p>
              </FadeUp>
              <FadeUp delay={0.5} className="mt-8 flex flex-wrap gap-4">
                <a
                  href={waLink(`Hi GrowMyRestro! I'm interested in your ${service.title} service. Can we discuss?`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-whatsapp-cta"
                  className="inline-flex items-center gap-2 text-cream font-bold px-7 py-3.5 rounded-full transition-transform duration-300 hover:scale-[1.03]"
                  style={{ background: service.color }}
                >
                  Discuss this service <ArrowRight className="w-4 h-4" />
                </a>
              </FadeUp>
            </div>
            <FadeUp delay={0.25} className="relative">
              <div className="absolute -inset-4 rounded-[2.5rem] border border-dashed rotate-2" style={{ borderColor: service.color }} />
              <div className="relative rounded-[2.5rem] overflow-hidden shadow-[0_32px_80px_rgba(45,18,77,0.2)] aspect-[4/3]">
                <img src={service.heroImg} alt={service.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-aubergine-deep/30 to-transparent" />
              </div>
              <div className="hospitality-card absolute -bottom-6 left-6 right-6 sm:left-10 sm:right-auto bg-white rounded-2xl border border-line shadow-[0_20px_50px_rgba(45,18,77,0.16)] px-6 py-4 flex gap-8">
                {service.stats.map((st) => (
                  <div key={st.label}>
                    <p className="font-display font-extrabold text-xl sm:text-2xl" style={{ color: service.color }}>{st.value}</p>
                    <p className="text-[11px] text-ink-muted mt-0.5">{st.label}</p>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="hospitality-background py-12 sm:py-16 lg:py-20">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14">
          <FadeUp className="max-w-2xl mb-12">
            <span className="font-mono uppercase text-xs tracking-[0.22em] font-semibold inline-flex items-center gap-2" style={{ color: service.color }}>
              <span className="w-8 h-px inline-block" style={{ background: service.color }} /> What's included
            </span>
            <h2 className="mt-5 font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-aubergine">
              Everything in this practice
            </h2>
          </FadeUp>
          <div className="grid md:grid-cols-2 gap-5">
            {service.subServices.map((sub, i) => (
              <FadeUp key={sub.name} delay={(i % 2) * 0.08}>
                <div className="hospitality-card group h-full bg-white rounded-3xl border border-line p-7 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(45,18,77,0.1)] transition-all duration-300" data-testid={`sub-service-${i}`}>
                  <div className="flex items-start gap-5">
                    <span className="font-mono text-sm font-semibold pt-0.5 text-saffron-warm">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h3 className="font-display font-bold text-lg text-aubergine">{sub.name}</h3>
                      <p className="mt-2.5 text-sm text-ink-muted leading-relaxed">{sub.desc}</p>
                    </div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="hospitality-background py-12 sm:py-16 lg:py-20  grain-overlay relative overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 grid lg:grid-cols-2 gap-8 lg:gap-12">
          <div>
            <FadeUp className="mb-10">
              <span className="font-mono uppercase text-xs tracking-[0.22em] font-semibold inline-flex items-center gap-2" style={{ color: service.color }}>
                <span className="w-8 h-px inline-block" style={{ background: service.color }} /> How we work
              </span>
              <h2 className="mt-5 font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-aubergine">The process, step by step</h2>
            </FadeUp>
            <div className="space-y-0">
              {service.process.map((p, i) => (
                <FadeUp key={p.step} delay={i * 0.08}>
                  <div className="flex gap-6 group" data-testid={`process-step-${i}`}>
                    <div className="flex flex-col items-center">
                      <span className="w-11 h-11 rounded-full flex items-center justify-center font-display font-extrabold text-sm text-cream shrink-0" style={{ background: service.color }}>
                        {i + 1}
                      </span>
                      {i < service.process.length - 1 && <span className="w-px flex-1 my-1" style={{ background: `${service.color}40` }} />}
                    </div>
                    <div className="pb-10">
                      <h3 className="font-display font-bold text-lg text-aubergine pt-2">{p.step}</h3>
                      <p className="mt-2 text-sm text-ink-muted leading-relaxed">{p.desc}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
          <div>
            <FadeUp className="mb-10">
              <span className="font-mono uppercase text-xs tracking-[0.22em] font-semibold inline-flex items-center gap-2" style={{ color: service.color }}>
                <span className="w-8 h-px inline-block" style={{ background: service.color }} /> Common questions
              </span>
              <h2 className="mt-5 font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-aubergine">Good to know</h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <FAQ items={service.faqs} color={service.color} />
            </FadeUp>
            <FadeUp delay={0.15}>
              <div className="mt-8 rounded-3xl p-7 text-cream relative overflow-hidden" style={{ background: service.color }}>
                <Check className="w-8 h-8 mb-4 opacity-80" />
                <p className="font-display font-bold text-xl leading-snug">Every engagement starts with a free, no-pressure audit of your venue.</p>
                <a
                  href={waLink(`Hi GrowMyRestro! I'd like a free audit for my venue, focused on ${service.title}.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-faq-audit-cta"
                  className="mt-5 inline-flex items-center gap-2 bg-cream font-bold px-6 py-3 rounded-full text-sm transition-transform duration-300 hover:scale-[1.03]"
                  style={{ color: service.color }}
                >
                  Get my free audit <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="hospitality-background py-12 sm:py-16 lg:py-20">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14">
          <FadeUp className="mb-10">
            <h2 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl text-aubergine">Explore our other services</h2>
          </FadeUp>
          <div className="grid sm:grid-cols-3 gap-5">
            {others.map((o) => {
              const OIcon = SERVICE_ICONS[o.icon];
              return (
                <FadeUp key={o.id}>
                  <Link
                    to={o.slug}
                    data-testid={`other-service-${o.id}`}
                    className="hospitality-card group block bg-white rounded-3xl border border-line p-7 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(45,18,77,0.1)] transition-all duration-300"
                  >
                    <span className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: o.color }}>
                      <OIcon className="w-5 h-5 text-cream" />
                    </span>
                    <h3 className="mt-5 font-display font-bold text-lg text-aubergine group-hover:text-terracotta transition-colors">{o.title}</h3>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: o.color }}>
                      View service <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </Link>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection
        title={`Ready to grow with ${service.title.toLowerCase()}?`}
        sub="One conversation is all it takes to see what your venue could become. Free, friendly and full of ideas."
      />
    </main>
  );
}
